import { useState, useEffect } from 'react'

export interface PricingEntry {
  price: string
  comparePrice: string
  currency: string
  locale: string
}

export interface PricingInfo extends PricingEntry {
  countryCode: string
  loading: boolean
}

// Easy to extend: add a new country code + pricing entry
export const PRICING_MAP: Record<string, PricingEntry> = {
  CL: {
    price: '$15.990',
    comparePrice: '$35.200',
    currency: 'CLP',
    locale: 'es-CL',
  },
  ES: {
    price: '16,90\u00a0€',
    comparePrice: '39,99\u00a0€',
    currency: 'EUR',
    locale: 'es-ES',
  },
  UY: {
    price: '$699',
    comparePrice: '$1.574',
    currency: 'UYU',
    locale: 'es-UY',
  },
  CO: {
    price: '$59.990',
    comparePrice: '$97.890',
    currency: 'COP',
    locale: 'es-CO',
  },
  MX: { price: '$296', comparePrice: '$696', currency: 'MXN', locale: 'es-MX' },
}

export const DEFAULT_PRICING: PricingEntry = {
  price: '$17',
  comparePrice: '$68',
  currency: 'USD',
  locale: 'en-US',
}

async function detectCountry(): Promise<string> {
  // Primary: ipapi.co (free, no key)
  try {
    const res = await fetch('https://ipapi.co/country/', {
      signal: AbortSignal.timeout(3000),
    })
    if (res.ok) {
      const code = (await res.text()).trim()
      if (code && code.length === 2) return code
    }
  } catch {
    // fall through to backup
  }

  // Backup: ip-api.com (free, no key)
  try {
    const res = await fetch('http://ip-api.com/json/?fields=countryCode', {
      signal: AbortSignal.timeout(3000),
    })
    if (res.ok) {
      const data = await res.json()
      if (data.countryCode) return data.countryCode
    }
  } catch {
    // fall through to default
  }

  return ''
}

export function usePricing(): PricingInfo {
  // Start as null to avoid flicker — render nothing until country is known
  const [result, setResult] = useState<PricingInfo | null>(null)

  useEffect(() => {
    detectCountry().then((countryCode) => {
      const entry = PRICING_MAP[countryCode] ?? DEFAULT_PRICING
      setResult({ ...entry, countryCode, loading: false })
    })
  }, [])

  // While loading, return default pricing with loading: true
  // Using default values prevents layout shift — prices are invisible until resolved
  if (!result) {
    return { ...DEFAULT_PRICING, countryCode: '', loading: true }
  }

  return result
}
